import { useState, useEffect } from 'react';

const BASE_URL = '/hubspot-api/crm/v3/objects';
const SEARCH_URL = '/hubspot-api/crm/v3/objects/deals/search';
const TOKEN = process.env.REACT_APP_HUBSPOT_TOKEN;

// Pipeline "Operations" del portal 26806608 (España)
const OPERATIONS_PIPELINE = '129134527';

// Campos del deal que necesitamos — mínimo posible para reducir payload
const DEAL_PROPERTIES = [
  'dealname', // contiene el UniqueID (SP-XXX-XXX-XXXXXX)
];

// Cabeceras comunes para todas las llamadas a HubSpot
const hsHeaders = () => ({
  Authorization: `Bearer ${TOKEN}`,
  'Content-Type': 'application/json',
});

// Extrae el UniqueID del cliente desde el dealname.
// Formato esperado: "OPS - Nombre Cliente - DD/MM/YY - SP-XXX-XXX-XXXXXX"
const parseUniqueId = (dealname = '') => {
  const match = dealname.match(/\b(SP-[A-Z0-9]{3}-[A-Z0-9]{3}-\d{6})\b/);
  return match ? match[1] : null;
};

// Dado un array de contactIds, devuelve Map<contactId, "Nombre Apellido">.
// Divide en chunks de 100 para respetar el límite de HubSpot.
const batchFetchContacts = async (contactIds) => {
  if (!contactIds.length) return new Map();
  const chunks = [];
  for (let i = 0; i < contactIds.length; i += 100) {
    chunks.push(contactIds.slice(i, i + 100));
  }
  const allResults = new Map();
  for (const chunk of chunks) {
    const body = {
      inputs: chunk.map(id => ({ id: String(id) })),
      properties: ['firstname', 'lastname'],
    };
    const res = await fetch(`${BASE_URL}/contacts/batch/read`, {
      method: 'POST',
      headers: hsHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
        // silencioso — el resultado simplemente queda sin nombre
      continue;
    }
    const data = await res.json();
    for (const c of data.results ?? []) {
      const { firstname = '', lastname = '' } = c.properties ?? {};
      allResults.set(String(c.id), [firstname, lastname].filter(Boolean).join(' ') || null);
    }
  }
  return allResults;
};

// Dado un array de dealIds, devuelve Map<dealId, propertyUniqueId(SP11XXXXXX)>.
// Primero obtiene la listing "Reserved" (typeId=55) de cada deal, luego
// resuelve su campo ops___property_unique_id (ej: "SP11021178") que es el Property UniqueID.
const batchFetchReservedListings = async (dealIds) => {
  if (!dealIds.length) return new Map();

  // Paso A: asociaciones deal → listing (quedarse solo con la "Reserved")
  const assocRes = await fetch(`/hubspot-api/crm/v4/associations/deals/listings/batch/read`, {
    method: 'POST',
    headers: hsHeaders(),
    body: JSON.stringify({ inputs: dealIds.map(id => ({ id: String(id) })) }),
  });
  if (!assocRes.ok) return new Map();
  const assocData = await assocRes.json();

  // dealId → listingId (solo Reserved)
  const dealToListing = new Map();
  for (const result of assocData.results ?? []) {
    const reserved = (result.to ?? []).find(t =>
      t.associationTypes?.some(a => a.typeId === 55)
    );
    if (reserved) dealToListing.set(String(result.from.id), String(reserved.toObjectId));
  }

  if (!dealToListing.size) return new Map();

  // Paso B: batch read de listings en chunks de 100 (límite de HubSpot)
  const listingIds = [...new Set(dealToListing.values())];
  const listingToUniqueId = new Map();
  for (let i = 0; i < listingIds.length; i += 100) {
    const chunk = listingIds.slice(i, i + 100);
    const listingRes = await fetch(`${BASE_URL}/listings/batch/read`, {
      method: 'POST',
      headers: hsHeaders(),
      body: JSON.stringify({
        inputs: chunk.map(id => ({ id })),
        properties: ['ops___property_unique_id'],
      }),
    });
    if (listingRes.ok) {
      const listingData = await listingRes.json();
      for (const l of listingData.results ?? []) {
        const uid = l.properties?.ops___property_unique_id;
        if (uid && uid !== 'null') listingToUniqueId.set(String(l.id), uid);
      }
    }
  }

  // Combinar: dealId → propertyUniqueId
  const map = new Map();
  for (const [dealId, listingId] of dealToListing.entries()) {
    const uid = listingToUniqueId.get(listingId);
    if (uid) map.set(dealId, uid);
  }
  return map;
};

// Dado un array de dealIds, devuelve Map<dealId, contactId> con el primer
// contacto asociado a cada deal (el comprador principal).
const batchFetchDealContacts = async (dealIds) => {
  if (!dealIds.length) return new Map();
  const res = await fetch(`/hubspot-api/crm/v4/associations/deals/contacts/batch/read`, {
    method: 'POST',
    headers: hsHeaders(),
    body: JSON.stringify({ inputs: dealIds.map(id => ({ id: String(id) })) }),
  });
  if (!res.ok) return new Map();
  const data = await res.json();
  const map = new Map();
  for (const result of data.results ?? []) {
    const first = result.to?.[0];
    if (first) map.set(String(result.from.id), String(first.toObjectId));
  }
  return map;
};

// Transforma un deal enriquecido al modelo que usa la app.
// _clientName y _propertyId se inyectan antes de llamar a esta función.
const mapDealToUnit = (deal) => {
  const p = deal.properties;
  return {
    id: deal.id,
    uniqueId: parseUniqueId(p.dealname),
    client: deal._clientName ?? null,
    propertyId: deal._propertyId ?? null,
  };
};

// Punto de entrada principal. Trae los deals de HubSpot con los siguientes filtros:
//
//   1. pipeline = Operations (129134527)
//      Solo deals del pipeline operativo de España — excluye Sales, SDRs, etc.
//
//   2. dealname CONTAINS "SP-"
//      Solo deals con UniqueID de cliente español en el nombre (SP-XXX-XXX-XXXXXX).
//      Descarta deals de Australia (AU-), India (IN-), Irlanda (IR-) y otros.
//
//   3. createdate >= 2025-10-01
//      Solo deals creados desde octubre de 2025 en adelante.
//
//   4. propertyId !== null (filtro en cliente, post-fetch)
//      Solo deals que tienen una listing con asociación "Reserved" (typeId=55)
//      y con el campo ops___property_unique_id relleno (SP11XXXXXX).
//      Descarta deals sin propiedad asignada o con listings solo "Interested".
//
// Ordenado por última modificación (más recientes primero).
// Paginación automática de 100 en 100 con pausa de 300ms entre páginas.
export const fetchOperationsDeals = async ({ maxPages = 10 } = {}) => {
  const rawDeals = [];
  let after = undefined;
  let page = 0;

  // 1. Paginar la búsqueda de deals
  while (page < maxPages) {
    const body = {
      filterGroups: [{
        filters: [
          { propertyName: 'pipeline', operator: 'EQ', value: OPERATIONS_PIPELINE },
          { propertyName: 'dealname', operator: 'CONTAINS_TOKEN', value: 'SP-' },
          { propertyName: 'createdate', operator: 'GTE', value: '2025-10-01' },
        ],
      }],
      properties: DEAL_PROPERTIES,
      sorts: [{ propertyName: 'hs_lastmodifieddate', direction: 'DESCENDING' }],
      limit: 100,
      ...(after ? { after } : {}),
    };

    const response = await fetch(SEARCH_URL, {
      method: 'POST',
      headers: hsHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`HubSpot search ${response.status}: ${response.statusText}`);

    const data = await response.json();
    rawDeals.push(...(data.results ?? []));

    after = data.paging?.next?.after;
    page++;
    if (!after) break;
    // Pequeña pausa entre páginas para evitar rate limit (429)
    await new Promise(r => setTimeout(r, 300));
  }

  const dealIds = rawDeals.map(d => d.id);

  // 2. Asociaciones en paralelo: deal→contact y deal→listing (2 llamadas)
  const [dealContactMap, dealListingMap] = await Promise.all([
    batchFetchDealContacts(dealIds),
    batchFetchReservedListings(dealIds),
  ]);

  // 3. Resolución de nombres de contacto (1 llamada batch)
  const contactIds = [...new Set([...dealContactMap.values()])];
  const contactNameMap = await batchFetchContacts(contactIds);

  // 4. Combinar todo — todas las keys son strings para garantizar el match
  const enriched = rawDeals.map(deal => {
    const contactId = dealContactMap.get(String(deal.id));
    return {
      ...deal,
      _clientName: contactId ? (contactNameMap.get(contactId) ?? null) : null,
      _propertyId: dealListingMap.get(String(deal.id)) ?? null,
    };
  });

  const mapped = enriched.map(mapDealToUnit).filter(d => d.propertyId !== null);
  console.log(`[HubSpot] ${mapped.length} deals cargados:`, mapped);
  return mapped;
};

// Hook React que expone los deals de Operations con estado de carga y error.
// Dado un propertyId (ej: "SP11022859"), busca en HubSpot el deal del pipeline
// Operations que tiene esa listing reservada y devuelve { client, uniqueId } o null.
export const fetchClientByPropertyId = async (propertyId) => {
  if (!propertyId) return null;

  // Paso 1: Buscar la listing con ese ops___property_unique_id
  const searchRes = await fetch(`${BASE_URL}/listings/search`, {
    method: 'POST',
    headers: hsHeaders(),
    body: JSON.stringify({
      filterGroups: [{
        filters: [{ propertyName: 'ops___property_unique_id', operator: 'EQ', value: propertyId }],
      }],
      properties: ['ops___property_unique_id'],
      limit: 5,
    }),
  });
  if (!searchRes.ok) return null;
  const searchData = await searchRes.json();
  const listing = searchData.results?.[0];
  if (!listing) return null;

  // Paso 2: Asociaciones listing → deals (typeId=55 = Reserved)
  const assocRes = await fetch(`/hubspot-api/crm/v4/associations/listings/deals/batch/read`, {
    method: 'POST',
    headers: hsHeaders(),
    body: JSON.stringify({ inputs: [{ id: String(listing.id) }] }),
  });
  if (!assocRes.ok) return null;
  const assocData = await assocRes.json();

  const dealAssocs = assocData.results?.[0]?.to ?? [];
  const reservedDeal = dealAssocs.find(t => t.associationTypes?.some(a => a.typeId === 55));
  const dealId = reservedDeal?.toObjectId ?? dealAssocs[0]?.toObjectId;
  if (!dealId) return null;

  // Paso 3: Contacto del deal
  const contactMap = await batchFetchDealContacts([String(dealId)]);
  const contactId = contactMap.get(String(dealId));
  if (!contactId) return null;

  // Paso 4: Nombre del contacto
  const nameMap = await batchFetchContacts([contactId]);
  const client = nameMap.get(contactId) ?? null;

  // Paso 5: uniqueId del deal (SP-XXX-XXX-XXXXXX del dealname)
  const dealRes = await fetch(`${BASE_URL}/deals/${dealId}?properties=dealname`, {
    headers: hsHeaders(),
  });
  let uniqueId = null;
  if (dealRes.ok) {
    const dealData = await dealRes.json();
    uniqueId = parseUniqueId(dealData.properties?.dealname ?? '');
  }

  return { client, uniqueId };
};

export const useHubSpotOperationsDeals = ({ maxPages = 10 } = {}) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchOperationsDeals({ maxPages })
      .then(data => {
        setDeals(data);
        setTotal(data.length);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [maxPages]);

  return { deals, loading, error, total };
};

// Fetch genérico GET para otros objetos de HubSpot (contactos, companies, deals legacy)
const hubspotFetch = async (endpoint, properties = [], limit = 100) => {
  const params = new URLSearchParams({ limit, properties: properties.join(',') });
  const response = await fetch(`${BASE_URL}/${endpoint}?${params}`, {
    headers: { Authorization: `Bearer ${TOKEN}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`HubSpot error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return data.results ?? [];
};

export const useHubSpotContacts = (limit = 100) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    hubspotFetch('contacts', ['firstname', 'lastname', 'email', 'phone', 'company'], limit)
      .then(setContacts)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [limit]);

  return { contacts, loading, error };
};

export const useHubSpotDeals = (limit = 100) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    hubspotFetch('deals', ['dealname', 'amount', 'dealstage', 'closedate', 'pipeline'], limit)
      .then(setDeals)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [limit]);

  return { deals, loading, error };
};

export const useHubSpotCompanies = (limit = 100) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    hubspotFetch('companies', ['name', 'domain', 'city', 'industry', 'phone'], limit)
      .then(setCompanies)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [limit]);

  return { companies, loading, error };
};
